import React from 'react';
import { Copy, Check, ExternalLink, LockKeyhole, Radio, ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import './ApiDocs.css';
import apiDocsData from '../data/apiDocs.json';

type Endpoint = {
  method: 'GET' | 'POST' | 'PATCH';
  path: string;
  summary: string;
  auth: string;
  details: string[];
  example?: string;
};

type EndpointGroup = {
  title: string;
  description: string;
  items: Endpoint[];
};

type ApiDocsData = {
  endpoints: EndpointGroup[];
  eventTypes: string[];
};

const { endpoints, eventTypes } = apiDocsData as ApiDocsData;

function CodeBlock({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard.writeText(code);
    setCopied(true);
    window.setTimeout(() => setCopied(false), 1600);
  };

  return (
    <div className="docs-code-wrap">
      <button className="docs-copy" onClick={copy} title="Copy example">
        {copied ? <Check size={15} /> : <Copy size={15} />}
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre><code>{code}</code></pre>
    </div>
  );
}

export default function ApiDocs() {
  return (
    <div className="api-docs">
      <div className="docs-hero">
        <div>
          <div className="docs-kicker"><Radio size={15} /> Developer reference</div>
          <h2>Telemetry API</h2>
          <p>Everything your assistive client needs to register, send telemetry, and power the admin dashboard.</p>
        </div>
        <div className="docs-status"><span /> API v1 · Operational</div>
      </div>

      <div className="docs-grid">
        <aside className="docs-toc">
          <p className="docs-label">On this page</p>
          <a href="#quickstart">Quick start</a>
          <a href="#endpoints">Endpoints</a>
          <a href="#events">Event types</a>
          <a href="#responses">Responses</a>
          <a href="#security">Security</a>
          <a className="docs-reference" href="/README.md">Full README <ExternalLink size={13} /></a>
        </aside>

        <div className="docs-content">
          <section id="quickstart" className="docs-section docs-quickstart">
            <div className="section-heading"><span className="section-number">01</span><div><h3>Quick start</h3><p>Authenticate once, then send batches over HTTPS.</p></div></div>
            <div className="quickstart-steps">
              <div><strong>01</strong><span>Register an installation</span><small>Receive a client secret and save it securely.</small></div>
              <div><strong>02</strong><span>Exchange credentials</span><small>Use the bearer token for ingestion requests.</small></div>
              <div><strong>03</strong><span>Upload telemetry</span><small>Include an idempotency key on every batch.</small></div>
            </div>
          </section>

          <section id="endpoints" className="docs-section">
            <div className="section-heading"><span className="section-number">02</span><div><h3>Endpoints</h3><p>Routes are grouped by the job they perform.</p></div></div>
            {endpoints.map((group) => (
              <div className="endpoint-group" key={group.title}>
                <div className="group-heading"><h4>{group.title}</h4><span>{group.items.length} routes</span></div>
                <p className="group-description">{group.description}</p>
                {group.items.map((endpoint) => (
                  <article className="endpoint" key={endpoint.path}>
                    <div className="endpoint-top"><span className={`method method-${endpoint.method.toLowerCase()}`}>{endpoint.method}</span><code>{endpoint.path}</code><span className="auth-pill"><LockKeyhole size={13} /> {endpoint.auth}</span></div>
                    <h4>{endpoint.summary}</h4>
                    <ul>{endpoint.details.map((detail) => <li key={detail}>{detail}</li>)}</ul>
                    {endpoint.example && <CodeBlock code={endpoint.example} />}
                  </article>
                ))}
              </div>
            ))}
          </section>

          <section id="events" className="docs-section">
            <div className="section-heading"><span className="section-number">03</span><div><h3>Event types</h3><p>Use the exact eventType value in each telemetry event.</p></div></div>
            <div className="event-list">{eventTypes.map((event, index) => <code key={event}><span>{String(index + 1).padStart(2, '0')}</span>{event}</code>)}</div>
          </section>

          <section id="responses" className="docs-section">
            <div className="section-heading"><span className="section-number">04</span><div><h3>Response envelope</h3><p>Every response includes a request ID for tracing.</p></div></div>
            <CodeBlock code={'{\n  "data": { },\n  "requestId": "uuid"\n}'} />
            <CodeBlock code={'{\n  "error": {\n    "code": "VALIDATION_ERROR",\n    "message": "The request could not be accepted.",\n    "fields": [{ "field": "Idempotency-Key", "issue": "Key was reused with a different payload." }],\n    "retryable": false\n  },\n  "requestId": "uuid"\n}'} />
            <div className="response-notes"><div><strong>Success</strong><span>Results appear under data. Collections may also include pagination.</span></div><div><strong>Error</strong><span>Errors include code, message, retryable, and optional fields.</span></div></div>
          </section>

          <section id="security" className="docs-section docs-security">
            <div className="section-heading"><span className="section-number">05</span><div><h3>Security notes</h3><p>Keep credentials and telemetry transport locked down.</p></div></div>
            <div className="security-grid"><div><ShieldCheck size={20} /><strong>Bearer tokens</strong><span>Short-lived JWTs are required for protected routes.</span></div><div><LockKeyhole size={20} /><strong>Idempotency</strong><span>Unique batch and event keys prevent duplicate writes and detect payload mismatch.</span></div><div><Radio size={20} /><strong>Rate limits</strong><span>API routes are currently limited per minute (600 requests / 60 seconds).</span></div></div>
          </section>
        </div>
      </div>
    </div>
  );
}
